/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Router,
  Route,
  Redirect,
  useParams,
  useHistory,
} from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';

import ApiOverview from './api/ApiOverview/index';
import ApiCredentials from './api/ApiCredentials/index';
import ApiTryOut from './api/ApiTryOut/index';
import ApiContracts from './api/ApiContracts/index';
import ApiDocumentation from './api/ApiDocumentation/index';
import ApiSdks from './api/ApiSdks/index';
import MainMenuDrawer from './shared/MainMenuDrawer/index';

import {
  ApplicationState,
  UserReducer,
  StorageOperations,
  NotificationReducer,
  OrganizationMetadataReducer,
  ApiReducer,
  KeyType,
} from '../data/interfaces';
import {
  fetchApiAuthenticated,
  fetchApiUnauthenticated,
  resetNotificationMsg,
} from '../data/operations';
import {
  RETURN_TO_PATH,
  SELECTED_ORG_UUID,
  ORG_QUERY_PARAM,
  FIDP,
  RESOURCE_SIGN_IN,
  PATH_SEPARATOR,
  CORRELATION_ID_QUERY_PARAM,
  CORRELATION_ID,
  NOT_FOUND_ERROR,
  SELECTED_ORG_HANDLE_NAME,
} from '../data/constants';
import {
  isProductionKeyGenerationAccessible,
  isSandboxKeyGenerationAccessible,
  localStoreOperation,
  sessionStoreOperation
} from '../data/utils';
import DevPortalFooter from './shared/DevPortalFooter';
import { Theme } from '@material-ui/core';
import ApiLatestVersionAvailableBanner from './api/ApiLatestVersionAvailableBanner';
import { PreLoaderLogo } from '../elements/PreLoaderLogo';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    height: '100vh',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  appBarSpacer: {
    minHeight: theme.spacing(8)
  },
  loaderContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
},
}));

export interface ApiProps {
  menuDrawerOpen: boolean;
  userReducer: UserReducer;
  apiReducer: ApiReducer;
  notificationReducer: NotificationReducer;
  organizationMetadataReducer: OrganizationMetadataReducer;
  fetchApiAuthenticated: (
    apiId: string,
    organizationId: string
  ) => Promise<void>;
  fetchApiUnauthenticated: (
    apiId: string,
    organizationId: string
  ) => Promise<void>;
  resetNotificationMsg: () => void;
}

function ApiPage({
  userReducer,
  notificationReducer,
  organizationMetadataReducer,
  apiReducer,
  menuDrawerOpen,
  fetchApiAuthenticated,
  fetchApiUnauthenticated,
  resetNotificationMsg,
}: ApiProps) {
  const classes = useStyles();
  const history = useHistory();
  const { orgName, apiId } = useParams<{ orgName: string; apiId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const {
    apimTokenObtained,
    orgs: userOrganizations,
    selectedOrg,
    user,
    signedIn
  } = userReducer;
  const allowedScopes = user.allowedScopes;
  const { type: notificationType, message: notification } = notificationReducer;
  const location = useLocation();
  const { error: apiFetchError, loading: apiLoading } = apiReducer;
  const [latestVersionInfoBannerOpen, setLatestVersionInfoBannerOpen] = useState(true);

  const checkUserAccessToCurrentPathOrg = (): boolean => {
    const selectedOrganization = userOrganizations.find(
      (userOrg) => userOrg.handle === orgName
    );
    return !!selectedOrganization;
  };

  const hasUserAccessToCurrentPathOrg = checkUserAccessToCurrentPathOrg();

  useEffect(() => {
    if (notification !== '') {
      enqueueSnackbar(notification, { variant: notificationType });
      resetNotificationMsg();
    }
  }, [notificationReducer]);

  useEffect(() => {
    if (apiFetchError !== '') {
      history.push(NOT_FOUND_ERROR);
    }
  }, [apiFetchError]);

  useEffect(() => {
    if (signedIn && orgName && !hasUserAccessToCurrentPathOrg) {
      history.push("/forbidden");
    }
  }, [selectedOrg]);

  useEffect(() => {
    if (!apimTokenObtained) {
      const queryParam = new URLSearchParams(location.search);
      const orgUuid = queryParam.get(ORG_QUERY_PARAM);
      if (organizationMetadataReducer.organizationMetadata.uuid != "") {
        fetchApiUnauthenticated(
          apiId,
          organizationMetadataReducer.organizationMetadata.uuid
        );
      }
      sessionStoreOperation(
        StorageOperations.ADD,
        RETURN_TO_PATH,
        window.location.pathname
      );

      const correlationId = queryParam.get(CORRELATION_ID_QUERY_PARAM);
      if (orgUuid) {
        localStoreOperation(StorageOperations.ADD, SELECTED_ORG_UUID, orgUuid);
      }
      if (correlationId) {
        localStoreOperation(StorageOperations.ADD, CORRELATION_ID, correlationId);
      }
      // Checking whether request is from choreo portal and setting fidp value
      if (queryParam.get('fidp')) {
        localStoreOperation(
          StorageOperations.ADD,
          FIDP,
          queryParam.get('fidp') ?? ''
        );
        history.push(RESOURCE_SIGN_IN);
      } else if (localStoreOperation(StorageOperations.GET, FIDP) && orgName == localStoreOperation(StorageOperations.GET, SELECTED_ORG_HANDLE_NAME)) {
        // Redirecting user to sigin if fidp value already exists in local store and orghandle in url and local store are equivalent
        history.push(RESOURCE_SIGN_IN);
      }
    } else if (
      apimTokenObtained &&
      selectedOrg &&
      hasUserAccessToCurrentPathOrg
    ) {
      if (apiId) {
        fetchApiAuthenticated(apiId, selectedOrg.uuid);
      } else {
        history.push(NOT_FOUND_ERROR)
      }
    }
  }, [apiId, orgName]);

  const getCredentialGenerationPath = () => {
    const pathList = [];
    if (isSandboxKeyGenerationAccessible(allowedScopes)) {
      pathList.push(
        PATH_SEPARATOR + orgName + '/apis/' + apiId + '/credentials/sandbox'
      );
    }
    if (isProductionKeyGenerationAccessible(allowedScopes)) {
      pathList.push(
        PATH_SEPARATOR + orgName + '/apis/' + apiId + '/credentials/production'
      );
    }
    return pathList;
  }

  return apiLoading ? (
    <div className={classes.root}>
      <div className={classes.loaderContainer}>
        <PreLoaderLogo size="medium" />
      </div>
    </div>
  ) : (
    <div className={classes.root}>
      <CssBaseline />
      <Router history={history}>
        <MainMenuDrawer menuDrawerOpen={menuDrawerOpen} organizationHandle={orgName} />
        <div className={classes.content}>
          <div className={classes.appBarSpacer} />
          <ApiLatestVersionAvailableBanner
            open={latestVersionInfoBannerOpen}
            close={() => setLatestVersionInfoBannerOpen(false)}
          />
          <Route exact path={PATH_SEPARATOR + orgName + '/apis/' + apiId}>
            <Redirect
              to={PATH_SEPARATOR + orgName + '/apis/' + apiId + '/overview'}
            />
          </Route>
          <Route path={PATH_SEPARATOR + orgName + '/apis/' + apiId + '/overview'}>
            <ApiOverview organizationHandle={orgName} />
          </Route>
          {apimTokenObtained && isSandboxKeyGenerationAccessible(allowedScopes) && (
            <Route path={PATH_SEPARATOR + orgName + '/apis/' + apiId + '/credentials/sandbox'}>
              <ApiCredentials keyType={KeyType.SANDBOX} />
            </Route>
          )}
          {apimTokenObtained && isProductionKeyGenerationAccessible(allowedScopes) && (
            <Route path={PATH_SEPARATOR + orgName + '/apis/' + apiId + '/credentials/production'}>
              <ApiCredentials keyType={KeyType.PRODUCTION} />
            </Route>
          )}
          {!apimTokenObtained &&
            <Route path={PATH_SEPARATOR + orgName + '/apis/' + apiId + '/resources'}>
              <ApiTryOut />
            </Route>
          }
          <Route path={PATH_SEPARATOR + orgName + '/apis/' + apiId + '/tryout'}>
            <ApiTryOut />
          </Route>
          {apimTokenObtained && (
            <Route
              path={PATH_SEPARATOR + orgName + '/apis/' + apiId + '/contracts'}
            >
              <ApiContracts />
            </Route>
          )}
          <Route
            path={
              PATH_SEPARATOR +
              orgName +
              '/apis/' +
              apiId +
              '/documents/:documentId?'
            }
          >
            <ApiDocumentation organizationHandle={orgName} />
          </Route>
          {apimTokenObtained && (
            <Route path={PATH_SEPARATOR + orgName + '/apis/' + apiId + '/sdks'}>
              <ApiSdks />
            </Route>
          )}
          <DevPortalFooter />
        </div>
      </Router>
    </div>
  );
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    userReducer: state.userReducer,
    apiReducer: state.apiReducer,
    notificationReducer: state.notificationReducer,
    organizationMetadataReducer: state.organizationMetadataReducer,
  };
};

const mapDispatchToProps = {
  fetchApiAuthenticated: fetchApiAuthenticated,
  fetchApiUnauthenticated: fetchApiUnauthenticated,
  resetNotificationMsg: resetNotificationMsg,
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiPage);
